import type { ReactNode } from "react"

import { UpdatePrompt } from "@/components/chat/update-prompt"
import { cn } from "@/lib/utils"

/**
 * Full-viewport shell every screen sits in.
 *
 * Height follows the visual viewport (`--app-height`, see lib/viewport.ts)
 * so the composer stays put when mobile browser chrome collapses or the
 * keyboard opens, and safe-area insets are respected rather than drawn — the
 * bottom one only while the keyboard is down, since iOS keeps reporting it
 * while the keyboard covers the home indicator.
 * A single-screen flow (sign-in, or the phone layout) is capped to a phone
 * column and centred; `wide` lets the two-pane workspace take the whole
 * window instead. The update prompt lives here so a new build is offered on
 * every screen, the sign-in page included.
 */
export function AppShell({
  children,
  className,
  wide = false,
}: {
  children: ReactNode
  className?: string
  wide?: boolean
}) {
  return (
    <div
      data-slot="app-shell"
      className="flex h-(--app-height,100dvh) justify-center bg-muted/40"
    >
      <div
        className={cn(
          "flex h-full w-full flex-col overflow-hidden bg-background",
          wide ? "max-w-none" : "max-w-lg sm:border-x",
          className
        )}
        style={{
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "var(--app-inset-bottom)",
        }}
      >
        <UpdatePrompt />
        {children}
      </div>
    </div>
  )
}
